import type { QuestionTypeCategory } from '@/contracts/questionnaire'
import type { QuestionType } from '@/types/form'
import {
  AlignLeft,
  Calculator,
  Calendar,
  CheckCircle2,
  CheckSquare,
  Clock3,
  FileSignature,
  FileUp,
  GalleryHorizontal,
  Globe2,
  GripVertical,
  Hash,
  Heading1,
  Heart,
  Info,
  ListFilter,
  ListTree,
  Mail,
  MapPin,
  MessageSquare,
  Minus,
  PanelTop,
  Phone,
  SlidersHorizontal,
  Star,
  Table2,
  ToggleLeft,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export type QuestionTypeMeta = {
  type: QuestionType
  zhName: string
  category: QuestionTypeCategory
  description: string
  icon: LucideIcon
  answerable: boolean
  popular?: boolean
}

export const questionTypeCategoryMeta: Array<{
  value: QuestionTypeCategory
  label: string
  description: string
}> = [
  { value: 'basic', label: '基本', description: '文字、聯絡方式與日期時間欄位。' },
  { value: 'choice', label: '選擇', description: '單選、多選、下拉與排序選項。' },
  { value: 'rating', label: '評分', description: '滿意度、推薦值與量表評分。' },
  { value: 'media', label: '媒體', description: '檔案、圖片與簽名類輸入。' },
  { value: 'layout', label: '版面', description: '說明段落與填答流程提示。' },
  { value: 'advanced', label: '進階', description: '複合表格與需要較多設定的題型。' },
]

export const questionTypeMeta: QuestionTypeMeta[] = [
  {
    type: 'SHORT_TEXT',
    zhName: '短文字',
    category: 'basic',
    description: '姓名、公司名稱、職稱等單行文字。',
    icon: MessageSquare,
    answerable: true,
    popular: true,
  },
  {
    type: 'LONG_TEXT',
    zhName: '長文字',
    category: 'basic',
    description: '開放意見、備註、需求描述與長篇回覆。',
    icon: AlignLeft,
    answerable: true,
    popular: true,
  },
  {
    type: 'EMAIL',
    zhName: '電子郵件',
    category: 'basic',
    description: 'Email 格式欄位，適合聯絡人或通知信。',
    icon: Mail,
    answerable: true,
    popular: true,
  },
  {
    type: 'PHONE_NUMBER',
    zhName: '電話號碼',
    category: 'basic',
    description: '含國碼與格式提示的電話欄位。',
    icon: Phone,
    answerable: true,
  },
  {
    type: 'TAIWAN_MOBILE',
    zhName: '台灣手機',
    category: 'basic',
    description: '台灣本地手機號碼欄位，不顯示國碼選擇。',
    icon: Phone,
    answerable: true,
  },
  {
    type: 'TAIWAN_LANDLINE',
    zhName: '台灣市話',
    category: 'basic',
    description: '選擇台灣市話區碼並輸入公司或據點市內電話。',
    icon: Phone,
    answerable: true,
  },
  {
    type: 'TAIWAN_ADDRESS',
    zhName: '台灣地址',
    category: 'basic',
    description: '縣市、區域、5 碼郵遞區號與詳細地址的分段欄位。',
    icon: MapPin,
    answerable: true,
  },
  {
    type: 'WEBSITE',
    zhName: '網站連結',
    category: 'basic',
    description: '公司網站、社群頁或公開資料連結。',
    icon: Globe2,
    answerable: true,
  },
  {
    type: 'NUMBER',
    zhName: '數字欄位',
    category: 'basic',
    description: '金額、數量、人數、坪數等需要數值範圍的欄位。',
    icon: Hash,
    answerable: true,
    popular: true,
  },
  {
    type: 'NUMBER_SLIDER',
    zhName: '數字滑桿',
    category: 'basic',
    description: '以滑桿快速選擇預算、評估分數或數量範圍。',
    icon: SlidersHorizontal,
    answerable: true,
  },
  {
    type: 'DATE',
    zhName: '日期',
    category: 'basic',
    description: '日期、月份、年份或日期時間選擇。',
    icon: Calendar,
    answerable: true,
  },
  {
    type: 'TIME',
    zhName: '時間',
    category: 'basic',
    description: '營業時間、預約時段或偏好聯繫時間。',
    icon: Clock3,
    answerable: true,
  },
  {
    type: 'MULTIPLE_CHOICE',
    zhName: '單選題',
    category: 'choice',
    description: '從多個選項中選擇一個答案。',
    icon: CheckCircle2,
    answerable: true,
    popular: true,
  },
  {
    type: 'CHECKBOXES',
    zhName: '多選題',
    category: 'choice',
    description: '可同時選擇多個答案，適合偏好與需求收集。',
    icon: CheckSquare,
    answerable: true,
    popular: true,
  },
  {
    type: 'DROPDOWN',
    zhName: '下拉選單',
    category: 'choice',
    description: '適合大量選項、地區、產業或固定代碼。',
    icon: ListFilter,
    answerable: true,
  },
  {
    type: 'NESTED_SELECT',
    zhName: '多層選單',
    category: 'choice',
    description: '適合國家/縣市/區域、產品線/型號等階層選擇。',
    icon: ListTree,
    answerable: true,
  },
  {
    type: 'PICTURE_CHOICE',
    zhName: '圖片選擇',
    category: 'choice',
    description: '以圖片卡片呈現產品、樣式或場景選項。',
    icon: GalleryHorizontal,
    answerable: true,
  },
  {
    type: 'YES_NO',
    zhName: '是非題',
    category: 'choice',
    description: '快速回答是或否，適合資格篩選。',
    icon: ToggleLeft,
    answerable: true,
  },
  {
    type: 'RANKING',
    zhName: '排序題',
    category: 'choice',
    description: '讓填答者拖拉排列優先順序。',
    icon: GripVertical,
    answerable: true,
  },
  {
    type: 'RATING',
    zhName: '評分題',
    category: 'rating',
    description: '星星、數字、愛心或表情符號評分。',
    icon: Star,
    answerable: true,
    popular: true,
  },
  {
    type: 'OPINION_SCALE',
    zhName: '意見量表',
    category: 'rating',
    description: '1 到 N 的水平量表，適合滿意度與同意程度。',
    icon: Hash,
    answerable: true,
  },
  {
    type: 'NPS',
    zhName: 'NPS 推薦值',
    category: 'rating',
    description: '0 到 10 推薦分數，自動支援 NPS 分析。',
    icon: Heart,
    answerable: true,
    popular: true,
  },
  {
    type: 'TOTAL',
    zhName: '加總計算',
    category: 'advanced',
    description: '顯示由前面數字題加總或平均的計算結果，供填答者確認。',
    icon: Calculator,
    answerable: true,
  },
  {
    type: 'FILE_UPLOAD',
    zhName: '檔案上傳',
    category: 'media',
    description: '收集合約、附件、圖片或其他佐證檔案。',
    icon: FileUp,
    answerable: true,
  },
  {
    type: 'SIGNATURE',
    zhName: '簽名',
    category: 'media',
    description: 'Canvas 簽名板，適合確認、授權或簽收。',
    icon: FileSignature,
    answerable: true,
  },
  {
    type: 'STATEMENT',
    zhName: '說明文字',
    category: 'layout',
    description: '只顯示內容不需填答，可用於段落說明或提示。',
    icon: PanelTop,
    answerable: false,
  },
  {
    type: 'SECTION_TITLE',
    zhName: '區段標題',
    category: 'layout',
    description: '切換到下一段問題前的大標題，用於分段與填答節奏。',
    icon: Heading1,
    answerable: false,
    popular: true,
  },
  {
    type: 'INTRO_BLOCK',
    zhName: '說明區塊',
    category: 'layout',
    description: '用醒目的提示卡呈現填答說明、注意事項或承諾文字。',
    icon: Info,
    answerable: false,
  },
  {
    type: 'DIVIDER',
    zhName: '分隔線',
    category: 'layout',
    description: '在填答流程中加入分隔與小標籤，讓段落更清楚。',
    icon: Minus,
    answerable: false,
  },
  {
    type: 'MATRIX',
    zhName: '矩陣題',
    category: 'advanced',
    description: '多個評估項目共用同一組量表或選項。',
    icon: Table2,
    answerable: true,
  },
]

export const questionTypeMetaByType = new Map(
  questionTypeMeta.map((meta) => [meta.type, meta] as const),
)

export function getQuestionTypeMeta(type: QuestionType) {
  return questionTypeMetaByType.get(type) ?? questionTypeMeta[0]
}

export function getQuestionTypeZhName(type: QuestionType) {
  return getQuestionTypeMeta(type).zhName
}

export function searchQuestionTypes(keyword: string) {
  const normalized = keyword.trim().toLocaleLowerCase()
  if (!normalized) return questionTypeMeta
  return questionTypeMeta.filter((meta) =>
    [meta.type, meta.zhName, meta.description].some((value) =>
      value.toLocaleLowerCase().includes(normalized),
    ),
  )
}
